<template>
  <div class="cbp">
    <label v-if="label" class="cbp-field-label">{{ label }}</label>

    <div v-if="!isExpanded" class="cbp-trigger" @click="toggleExpanded">
      <span class="cbp-trigger-text">{{ displayPlaceholder }}</span>
      <span class="cbp-trigger-arrow cbp-trigger-arrow--down"></span>
    </div>

    <transition name="cbp-expand">
      <div v-if="isExpanded" class="cbp-panel">
        <div class="cbp-search-row">
          <input
            :id="searchInputId"
            ref="searchInput"
            v-model="searchQuery"
            type="search"
            :placeholder="searchPlaceholder"
            class="cbp-search"
            @click.stop
          />
          <button type="button" class="cbp-collapse-btn" @click="toggleExpanded" title="Collapse">
            <span class="cbp-trigger-arrow cbp-trigger-arrow--up"></span>
          </button>
        </div>

        <div class="cbp-picker">
          <div v-if="!normalizedOptions.length" class="cbp-empty">
            <p class="cbp-empty-text">{{ emptyText }}</p>
          </div>
          <div v-else-if="!filteredOptions.length" class="cbp-empty">
            <p class="cbp-empty-text">{{ emptySearchText }}</p>
          </div>
          <ul v-else class="cbp-list" :class="{ 'cbp-list-compact': compact }">
            <li class="cbp-item cbp-item-select-all">
              <label class="cbp-item-label">
                <input
                  ref="selectAllCheckbox"
                  type="checkbox"
                  class="cbp-checkbox"
                  :checked="allSelected"
                  @change="toggleSelectAll"
                />
                <span class="cbp-item-copy">
                  <span class="cbp-item-title">{{ selectAllLabel }}</span>
                </span>
              </label>
            </li>
            <li v-for="item in filteredOptions" :key="item.key" class="cbp-item">
              <label class="cbp-item-label">
                <input
                  type="checkbox"
                  class="cbp-checkbox"
                  :checked="isSelected(item.value)"
                  @change="toggle(item.value, $event)"
                />
                <span class="cbp-item-copy">
                  <span class="cbp-item-title">{{ item.label }}</span>
                  <span v-if="item.description" class="cbp-item-desc">{{ item.description }}</span>
                </span>
              </label>
            </li>
          </ul>
        </div>
      </div>
    </transition>

    <div v-if="showStatus && normalizedOptions.length" class="cbp-status">
      <p class="cbp-status-text">
        <span class="cbp-status-count">{{ selectedCount }}</span>
        of
        <span class="cbp-status-count">{{ normalizedOptions.length }}</span>
        {{ itemWord }} selected
      </p>
      <div class="cbp-status-actions">
        <button
          v-if="!allSelected"
          type="button"
          class="cbp-status-link"
          @click="selectAll"
        >
          Select all
        </button>
        <button
          v-if="selectedCount"
          type="button"
          class="cbp-status-link"
          @click="clearSelection"
        >
          Clear selection
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "CheckboxPicker",
  props: {
    modelValue: {
      type: [Array, null],
      default: null,
    },
    options: {
      type: Array,
      default: function () {
        return [];
      },
    },
    label: {
      type: String,
      default: "",
    },
    searchPlaceholder: {
      type: String,
      default: "Search",
    },
    emptyText: {
      type: String,
      default: "No options available.",
    },
    emptySearchText: {
      type: String,
      default: "No options match your search.",
    },
    itemName: {
      type: String,
      default: "item",
    },
    compact: {
      type: Boolean,
      default: false,
    },
    showStatus: {
      type: Boolean,
      default: false,
    },
    collapsedPlaceholder: {
      type: String,
      default: "All",
    },
    selectAllLabel: {
      type: String,
      default: "Select All",
    },
    defaultCollapsed: {
      type: Boolean,
      default: true,
    },
  },
  emits: ["update:modelValue"],
  data: function () {
    return {
      searchQuery: "",
      isExpanded: !this.defaultCollapsed,
      instanceId: "cbp-" + Date.now() + "-" + Math.floor(Math.random() * 10000),
    };
  },
  computed: {
    searchInputId: function () {
      return this.instanceId + "-search";
    },
    isAllSelectedMode: function () {
      return this.modelValue === null;
    },
    selectedValues: function () {
      if (this.isAllSelectedMode) {
        return this.allOptionValues;
      }
      return Array.isArray(this.modelValue) ? this.modelValue : [];
    },
    allOptionValues: function () {
      var next = [];
      var list = this.normalizedOptions;
      for (var i = 0; i < list.length; i++) {
        next.push(list[i].value);
      }
      return next;
    },
    normalizedOptions: function () {
      var list = Array.isArray(this.options) ? this.options : [];
      var out = [];
      for (var i = 0; i < list.length; i++) {
        var option = list[i];
        if (option === null || option === undefined || option === "") {
          continue;
        }
        if (typeof option === "string" || typeof option === "number") {
          out.push({
            key: String(option),
            value: option,
            label: String(option),
            description: "",
          });
          continue;
        }
        var value = option.value !== undefined ? option.value : (option.id !== undefined ? option.id : option.name);
        var label = option.label || option.name || String(value);
        out.push({
          key: String(value),
          value: value,
          label: label,
          description: option.description || "",
        });
      }
      return out;
    },
    filteredOptions: function () {
      var list = this.normalizedOptions;
      var q = this.searchQuery ? String(this.searchQuery).trim().toLowerCase() : "";
      if (!q) {
        return list;
      }
      var filtered = [];
      for (var i = 0; i < list.length; i++) {
        var item = list[i];
        var label = item.label ? String(item.label).toLowerCase() : "";
        var description = item.description ? String(item.description).toLowerCase() : "";
        if (label.indexOf(q) !== -1 || description.indexOf(q) !== -1) {
          filtered.push(item);
        }
      }
      return filtered;
    },
    selectedCount: function () {
      if (this.isAllSelectedMode) {
        return this.normalizedOptions.length;
      }
      return this.selectedValues.length;
    },
    allSelected: function () {
      return this.normalizedOptions.length > 0 && this.selectedCount === this.normalizedOptions.length;
    },
    someSelected: function () {
      if (this.isAllSelectedMode) {
        return false;
      }
      return this.selectedCount > 0 && this.selectedCount < this.normalizedOptions.length;
    },
    itemWord: function () {
      var base = this.itemName || "item";
      return this.normalizedOptions.length === 1 ? base : base + "s";
    },
    displayPlaceholder: function () {
      if (this.allSelected || this.isAllSelectedMode) {
        return this.collapsedPlaceholder;
      }
      if (!this.selectedCount) {
        return "(None)";
      }

      var labels = [];
      var list = this.normalizedOptions;
      for (var i = 0; i < list.length; i++) {
        if (this.isSelected(list[i].value)) {
          labels.push(list[i].label);
        }
      }

      if (labels.length === 1) {
        return labels[0];
      }
      if (labels.length === 2) {
        return labels.join(", ");
      }
      return labels.length + " selected";
    },
  },
  watch: {
    someSelected: function () {
      this.syncSelectAllIndeterminate();
    },
    allSelected: function () {
      this.syncSelectAllIndeterminate();
    },
    isExpanded: function (expanded) {
      if (expanded) {
        var self = this;
        this.$nextTick(function () {
          self.syncSelectAllIndeterminate();
          if (self.$refs.searchInput) {
            self.$refs.searchInput.focus();
          }
        });
      } else {
        this.searchQuery = "";
      }
    },
  },
  mounted: function () {
    if (Array.isArray(this.modelValue) && this.modelValue.length === 0) {
      this.emitValue(null);
    }
    this.syncSelectAllIndeterminate();
  },
  updated: function () {
    this.syncSelectAllIndeterminate();
  },
  methods: {
    toggleExpanded: function () {
      this.isExpanded = !this.isExpanded;
    },
    syncSelectAllIndeterminate: function () {
      var el = this.$refs.selectAllCheckbox;
      if (el) {
        el.indeterminate = this.someSelected;
      }
    },
    isSelected: function (value) {
      if (this.isAllSelectedMode) {
        return true;
      }
      var selected = this.selectedValues;
      for (var i = 0; i < selected.length; i++) {
        if (String(selected[i]) === String(value)) {
          return true;
        }
      }
      return false;
    },
    emitValue: function (next) {
      this.$emit("update:modelValue", next);
    },
    emitFromSelection: function (next) {
      if (next.length === this.normalizedOptions.length) {
        this.emitValue(null);
        return;
      }
      this.emitValue(next);
    },
    toggle: function (value, event) {
      var checked = event && event.target ? event.target.checked : false;
      var base = this.isAllSelectedMode ? this.allOptionValues.slice() : this.selectedValues.slice();
      var next = [];

      for (var i = 0; i < base.length; i++) {
        if (String(base[i]) !== String(value)) {
          next.push(base[i]);
        }
      }
      if (checked) {
        next.push(value);
      }
      this.emitFromSelection(next);
    },
    toggleSelectAll: function (event) {
      var checked = event && event.target ? event.target.checked : false;
      if (checked) {
        this.selectAll();
        return;
      }
      this.clearSelection();
    },
    selectAll: function () {
      this.emitValue(null);
    },
    clearSelection: function () {
      this.emitValue([]);
    },
  },
};
</script>

<style scoped>
.cbp-field-label {
  display: block;
  margin-bottom: 0.5rem;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #6b7280;
}

.cbp-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  width: 100%;
  padding: 0.55rem 0.75rem;
  font-size: 0.8125rem;
  color: #1f2937;
  text-align: left;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  cursor: pointer;
}

.cbp-trigger:hover {
  border-color: #cbd5e1;
  background: #f8fafc;
}

.cbp-trigger-text {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cbp-trigger-arrow {
  flex-shrink: 0;
  display: inline-block;
  width: 0.45rem;
  height: 0.45rem;
  border-right: 2px solid #64748b;
  border-bottom: 2px solid #64748b;
}

.cbp-trigger-arrow--down {
  transform: rotate(45deg);
  margin-top: -0.1rem;
}

.cbp-trigger-arrow--up {
  transform: rotate(-135deg);
  margin-top: 0.15rem;
}

.cbp-panel {
  margin-top: 0.5rem;
}

.cbp-search-row {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  margin-bottom: 0.5rem;
}

.cbp-search {
  flex: 1;
  min-width: 0;
  padding: 0.5rem 0.75rem;
  font-size: 0.8125rem;
  color: #1f2937;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  outline: none;
}

.cbp-search:focus {
  border-color: #60a5fa;
}

.cbp-collapse-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  padding: 0;
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  cursor: pointer;
  flex-shrink: 0;
}

.cbp-collapse-btn:hover {
  background: #e2e8f0;
  border-color: #cbd5e1;
}

.cbp-picker {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.cbp-list {
  margin: 0;
  padding: 0;
  list-style: none;
  max-height: 18rem;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.cbp-list-compact {
  max-height: 12rem;
}

.cbp-item + .cbp-item {
  border-top: 1px solid #f1f5f9;
}

.cbp-item-select-all {
  background: #f8fafc;
}

.cbp-item-select-all .cbp-item-title {
  font-weight: 700;
}

.cbp-item-label {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.65rem 0.85rem;
  cursor: pointer;
}

.cbp-item-label:hover {
  background: #f8fafc;
}

.cbp-item-select-all .cbp-item-label:hover {
  background: #f1f5f9;
}

.cbp-checkbox {
  width: 1rem;
  height: 1rem;
  margin-top: 0.125rem;
  flex-shrink: 0;
  accent-color: #2563eb;
  cursor: pointer;
}

.cbp-item-copy {
  min-width: 0;
  text-align: left;
}

.cbp-item-title {
  display: block;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #111827;
  word-break: break-word;
}

.cbp-item-desc {
  display: block;
  margin-top: 0.125rem;
  font-size: 0.75rem;
  line-height: 1.4;
  color: #6b7280;
}

.cbp-empty {
  padding: 1.5rem 1rem;
  text-align: center;
}

.cbp-empty-text {
  margin: 0;
  font-size: 0.8125rem;
  color: #6b7280;
}

.cbp-expand-enter-active,
.cbp-expand-leave-active {
  overflow: hidden;
  transition: max-height 0.28s ease, opacity 0.22s ease, margin-top 0.28s ease;
}

.cbp-expand-enter-from,
.cbp-expand-leave-to {
  max-height: 0;
  opacity: 0;
  margin-top: 0;
}

.cbp-expand-enter-to,
.cbp-expand-leave-from {
  max-height: 24rem;
  opacity: 1;
  margin-top: 0.5rem;
}

.cbp-status {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-top: 0.75rem;
  padding: 0.75rem 1rem;
  border-radius: 0.75rem;
  border: 1px solid #dbeafe;
  background: #eff6ff;
}

.cbp-status-text {
  margin: 0;
  font-size: 0.8125rem;
  color: #1e3a8a;
}

.cbp-status-count {
  font-weight: 700;
}

.cbp-status-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.cbp-status-link {
  padding: 0;
  font-size: 0.75rem;
  font-weight: 600;
  color: #1e40af;
  background: transparent;
  border: 0;
  cursor: pointer;
}

.cbp-status-link:hover {
  text-decoration: underline;
}
</style>
